/**
 * Supabase Auth Provider
 * Handles authentication via Supabase (Google OAuth)
 * 
 * This file isolates all Supabase-specific auth logic.
 * App code should NEVER import this directly - use src/auth/index.js instead.
 */

import { createClient } from '@supabase/supabase-js';
import { SUPABASE_URL, SUPABASE_ANON_KEY, AUTH_CALLBACK_PATH, isSupabaseConfigured } from '../authConfig';

// Create Supabase client (singleton)
let supabaseClient = null;

function getSupabaseClient() {
  if (!supabaseClient && isSupabaseConfigured()) {
    supabaseClient = createClient(SUPABASE_URL, SUPABASE_ANON_KEY, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
      },
    });
  }
  return supabaseClient;
}

/**
 * Initiate Google login via Supabase OAuth
 * @returns {Promise<{error: Error|null}>}
 */
export async function loginWithGoogle() {
  const supabase = getSupabaseClient();
  if (!supabase) {
    console.error('[SupabaseAuth] Supabase not configured');
    return { error: new Error('Supabase not configured') };
  }

  const redirectTo = `${window.location.origin}${AUTH_CALLBACK_PATH}`;
  console.log('[SupabaseAuth] Starting Google OAuth, redirect:', redirectTo);

  return await supabase.auth.signInWithOAuth({
    provider: 'google',
    options: { redirectTo }
  });
}

/**
 * Handle OAuth callback from Supabase
 * @param {string} apiUrl - Backend API URL
 * @returns {Promise<{success: boolean, user: object|null, error: string|null}>}
 */
export async function handleAuthCallback(apiUrl) {
  const supabase = getSupabaseClient();
  if (!supabase) {
    return { success: false, user: null, error: 'Supabase not configured' };
  }

  try {
    // Get session - Supabase auto-processes URL tokens via detectSessionInUrl
    let { data: { session }, error } = await supabase.auth.getSession();

    if (error) {
      console.error('[SupabaseAuth] getSession error:', error);
      return { success: false, user: null, error: error.message };
    }

    // If no session, try PKCE code exchange
    if (!session) {
      const urlParams = new URLSearchParams(window.location.search);
      const code = urlParams.get('code');
      if (code) {
        console.log('[SupabaseAuth] Found code in URL, exchanging via PKCE...');
        const { data, error: exchangeError } = await supabase.auth.exchangeCodeForSession(
          window.location.href
        );
        if (exchangeError) {
          console.error('[SupabaseAuth] exchangeCodeForSession error:', exchangeError);
          return { success: false, user: null, error: exchangeError.message };
        }
        session = data.session;
      }
    }

    if (!session) {
      return { success: false, user: null, error: 'No session obtained' };
    }

    console.log('[SupabaseAuth] Session obtained, syncing with backend...');
    
    // Sync with backend
    const user = await syncSessionWithBackend(apiUrl, session);
    if (!user) {
      return { success: false, user: null, error: 'Backend sync failed' };
    }

    // Clear URL tokens
    window.history.replaceState(null, '', AUTH_CALLBACK_PATH);

    return { success: true, user, error: null };
  } catch (error) {
    console.error('[SupabaseAuth] handleAuthCallback error:', error);
    return { success: false, user: null, error: error.message };
  }
}

/**
 * Sync Supabase session with backend
 * @param {string} apiUrl - Backend API URL
 * @param {object} session - Supabase session
 * @returns {Promise<object|null>}
 */
export async function syncSessionWithBackend(apiUrl, session) {
  try {
    const response = await fetch(`${apiUrl}/auth/supabase-session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`
      },
      credentials: 'include',
      body: JSON.stringify({
        access_token: session.access_token,
        user: session.user
      })
    });

    // Read body exactly ONCE as text, then parse
    const responseText = await response.text();
    
    if (response.ok) {
      try {
        const user = JSON.parse(responseText);
        if (user.token) {
          localStorage.setItem('auth_token', user.token);
        }
        return user;
      } catch (parseError) {
        console.error('[SupabaseAuth] Failed to parse response:', parseError);
        return null;
      }
    }
    
    console.error('[SupabaseAuth] Backend sync failed:', response.status, responseText);
    return null;
  } catch (error) {
    console.error('[SupabaseAuth] syncSessionWithBackend error:', error);
    return null;
  }
}

/**
 * Get current user from Supabase session
 * @param {string} apiUrl - Backend API URL
 * @returns {Promise<object|null>}
 */
export async function getCurrentUser(apiUrl) {
  const supabase = getSupabaseClient();
  if (!supabase) return null;

    try {
    // P0 FIX: If a valid backend token already exists (from email/password login),
    // do NOT let a stale Supabase session override it.
    // Only use Supabase session if there is no existing backend token.
    const existingToken = localStorage.getItem('auth_token');
    
    if (!existingToken) {
      const { data: { session } } = await supabase.auth.getSession();
      
      if (session) {
        // Sync with backend to get app user data
        return await syncSessionWithBackend(apiUrl, session);
      }
    }
    
    // Fallback to backend session check
    const response = await fetch(`${apiUrl}/auth/me`, {
      credentials: 'include',
      headers: {
        'Authorization': `Bearer ${localStorage.getItem('auth_token') || ''}`
      }
    });
    
    // For non-OK responses, return null immediately - don't read body
    if (!response.ok) {
      return null;
    }
    
    // Only read body for successful responses
    try {
      return await response.json();
    } catch (parseError) {
      console.error('[SupabaseAuth] Failed to parse /auth/me response:', parseError);
      return null;
    }
  } catch (error) {
    console.error('[SupabaseAuth] getCurrentUser error:', error);
    return null;
  }
}

/**
 * Logout from Supabase and backend
 * @param {string} apiUrl - Backend API URL
 */
export async function logout(apiUrl) {
  const supabase = getSupabaseClient();
  
  try {
    // Logout from Supabase
    if (supabase) {
      await supabase.auth.signOut();
    }
    
    // Logout from backend
    await fetch(`${apiUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include'
    });
    
    localStorage.removeItem('auth_token');
  } catch (error) {
    console.error('[SupabaseAuth] logout error:', error);
  }
}

/**
 * Check if user is authenticated via Supabase
 * @returns {Promise<boolean>}
 */
export async function isAuthenticated() {
  const supabase = getSupabaseClient();
  if (!supabase) return false;

  const { data: { session } } = await supabase.auth.getSession();
  return !!session;
}

/**
 * Get Supabase client for advanced use cases
 * @returns {SupabaseClient|null}
 */
export function getClient() {
  return getSupabaseClient();
}

/**
 * Subscribe to auth state changes
 * @param {function} callback - Callback for auth state changes
 * @returns {function} Unsubscribe function
 */
export function onAuthStateChange(callback) {
  const supabase = getSupabaseClient();
  if (!supabase) return () => {};

  const { data: { subscription } } = supabase.auth.onAuthStateChange(callback);
  return () => subscription?.unsubscribe();
}
